import React from 'react';
import {
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Switch,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { locationsApi } from '../../api/services';
import type { Device, Location } from '../../api/config';
import { FormDialog } from '../FormDialog';

interface DeviceFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Device>) => void;
  initialData?: Device;
  prefilledLocationId?: number;
  onExited?: () => void;
  error?: string | null;
  isPending?: boolean;
}

const DEFAULT_FORM_DATA: Partial<Device> = {
  device_id: '',
  location_id: null,
  label: '',
  power_configuration: 'Unknown',
  staging: true,
};

export const DeviceForm: React.FC<DeviceFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  prefilledLocationId,
  onExited,
  error: apiError,
  isPending,
}) => {
  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationsApi.getAll(),
    enabled: open,
  });

  const [formData, setFormData] = React.useState<Partial<Device>>({
    ...DEFAULT_FORM_DATA,
    ...initialData,
  });

  const [errors, setErrors] = React.useState<{ device_id?: string }>({});
  const [confirmingStaging, setConfirmingStaging] = React.useState(false);

  React.useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else if (open) {
      setFormData({
        ...DEFAULT_FORM_DATA,
        location_id: prefilledLocationId ?? null,
      });
    }
  }, [initialData, open, prefilledLocationId]);

  const handleDeviceIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const transformedValue = e.target.value.toLowerCase().trim();
    setFormData((prev) => ({ ...prev, device_id: transformedValue }));
    if (errors.device_id) setErrors({});
  };

  const selectedLocation = locations.find((l: Location) => l.id === formData.location_id) ?? null;

  // The customer-deployed → staging transition is the one that needs a
  // confirmation prompt: it stops producing inventory state at an active
  // location, which is destructive. Other transitions (off→on at unassigned
  // or inactive locations, on→off, new-device default) are routine.
  const needsStagingConfirmation =
    !!initialData &&
    initialData.staging === false &&
    formData.staging === true &&
    formData.location_id !== null &&
    !!selectedLocation?.active;

  const proceedToSubmit = () => {
    setErrors({});

    const deviceId = formData.device_id?.trim();
    if (!deviceId) return;

    if (!/^[a-f0-9]{24}$/i.test(deviceId)) {
      setErrors({ device_id: 'Device ID must be a 24-character hex string (Particle coreid)' });
      return;
    }

    onSubmit({
      ...formData,
      device_id: deviceId.toLowerCase(),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (needsStagingConfirmation) {
      setConfirmingStaging(true);
      return;
    }
    proceedToSubmit();
  };

  const handleConfirmStaging = () => {
    setConfirmingStaging(false);
    proceedToSubmit();
  };

  return (
    <>
      <FormDialog
        open={open}
        onClose={onClose}
        onSubmit={handleSubmit}
        title={initialData ? 'Edit Device' : 'Add Device'}
        submitLabel={initialData ? 'Update' : 'Create'}
        error={apiError}
        isPending={isPending}
        TransitionProps={{ onExited }}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            name="device_id"
            label="Device ID"
            value={formData.device_id}
            onChange={handleDeviceIdChange}
            fullWidth
            required
            disabled={!!initialData}
            error={!!errors.device_id}
            helperText={errors.device_id || 'Particle coreid (e.g., e00fce688de07281162e5d93)'}
          />
          <Autocomplete
            options={locations}
            value={selectedLocation}
            onChange={(_e, newValue) => {
              setFormData((prev) => ({ ...prev, location_id: newValue?.id ?? null }));
            }}
            getOptionLabel={(option: Location) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Location"
                helperText="Optional. A device can be in staging with or without a location."
              />
            )}
          />
          <TextField
            name="label"
            label="Label"
            value={formData.label || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, label: e.target.value }))}
            fullWidth
            helperText='Optional — e.g., "front", "back"'
          />
          <FormControl fullWidth>
            <InputLabel>Power Configuration</InputLabel>
            <Select
              name="power_configuration"
              value={formData.power_configuration || 'Unknown'}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  power_configuration: e.target.value as Device['power_configuration'],
                }))
              }
              label="Power Configuration"
            >
              <MenuItem value="Unknown">Unknown</MenuItem>
              <MenuItem value="Mains-Power">Mains-Power</MenuItem>
              <MenuItem value="Solar">Solar</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={!!formData.staging}
                onChange={(e) => setFormData((prev) => ({ ...prev, staging: e.target.checked }))}
              />
            }
            label="Staging mode"
          />
          <Typography variant="caption" color="text.secondary">
            Staging devices keep producing sensor diagnostics but do not generate
            inventory state or beacon concerns. Turn off when commissioning the device.
          </Typography>
        </Stack>
      </FormDialog>
      <Dialog open={confirmingStaging} onClose={() => setConfirmingStaging(false)}>
        <DialogTitle>Move device into staging?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will stop producing inventory state and beacon concerns at{' '}
            <strong>{selectedLocation?.name ?? 'this location'}</strong>. Sensor
            diagnostics will keep flowing. You can flip staging back off at any
            time to resume operation.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingStaging(false)}>Cancel</Button>
          <Button onClick={handleConfirmStaging} variant="contained" color="warning">
            Move to staging
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
